import { useCallback, useContext, useEffect, useState } from 'react'
import TransactionsContext from 'selfServicePortal/contexts/TransactionsContext'
import { ITransaction } from 'selfServicePortal/interfaces/i-api-creaditCard'

export enum ActiveSort {
	Asc,
	Desc,
	None,
}

export const useTransactions = () => {
	const { transactions } = useContext(TransactionsContext)

	const [hideShowMore, setHideShowMore] = useState(false)
	const [tableData, setTableData] = useState<any[]>([])
	const [activeSortByItem, setActiveSortByItem] = useState<
		string | undefined
	>(undefined)
	const [activeArrow, setActiveArrow] = useState<ActiveSort>(ActiveSort.None)
	const [error, setError] = useState<Error | undefined>()
	const [monthsTransactions, setMonthsTransactions] = useState<any[]>([])

	const sortData = useCallback(
		(
			data: ITransaction[],
			sortOn: keyof ITransaction,
			order: 'asc' | 'desc',
		) => {
			return [...data].sort((a, b) => {
				const valueA = a[sortOn]
				const valueB = b[sortOn]

				if (typeof valueA === 'string' && typeof valueB === 'string') {
					return order === 'asc'
						? valueA.localeCompare(valueB)
						: valueB.localeCompare(valueA)
				}

				if (typeof valueA === 'number' && typeof valueB === 'number') {
					return order === 'asc' ? valueA - valueB : valueB - valueA
				}
				return 0
			})
		},
		[],
	)

	const sortTransactions = useCallback(
		(sortOn: keyof ITransaction, order: 'asc' | 'desc') => {
			const sortedData = sortData(tableData, sortOn, order)
			setTableData(sortedData)
			setActiveSortByItem(sortOn)
			setActiveArrow(order === 'asc' ? ActiveSort.Asc : ActiveSort.Desc)
		},
		[tableData, sortData],
	)

	const getMoreTransactionsFromMonth = useCallback(
		(date: string, sliceTo: number) => {
			let month = new Date(date).getMonth()
			let year = new Date(date).getFullYear()

			const newArr = transactions?.filter((i) => {
				const transactionDate = new Date(i.transactionDate)
				return (
					transactionDate.getMonth() === month &&
					transactionDate.getFullYear() === year
				)
			})

			if (newArr) {
				const sortedData = sortData(newArr, 'transactionDate', 'desc')
				setTableData(sortedData.slice(0, sliceTo))
				setMonthsTransactions(sortedData)
			}
		},
		[transactions, sortData],
	)

	useEffect(() => {
		if (transactions && transactions.length > 0) {
			getMoreTransactionsFromMonth(transactions[0].transactionDate, 10)
		} else if (transactions?.length === 0) {
			setError(new Error('NoTransactions'))
		}
		// eslint-disable-next-line
	}, [transactions])

	useEffect(() => {
		setHideShowMore(monthsTransactions.length > tableData.length)
	}, [monthsTransactions, tableData])

	return {
		sortTransactions,
		getMoreTransactionsFromMonth,
		tableData,
		hideShowMore,
		error,
		setTableData,
		activeSortByItem,
		activeArrow,
		setActiveArrow,
	}
}
